import { BaseSerializer } from '@core/serializers/baseSerializer';
import {
  WALLET_STATUS,
  WALLET_TRANSACTION_TYPES,
  WALLET_PAYMENT_METHODS,
  FEDAPAY_TRANSACTION_STATUS,
  WALLET_ERROR_CODES,
  WALLET_DEFAULT_CURRENCY
} from './constants.js';

/**
 * WalletSerializer V3
 * Transformation API <-> UI pour toutes les opérations wallet
 */
class WalletSerializer extends BaseSerializer {
  // ============================================
  // SERIALIZE: UI -> API
  // ============================================

  /**
   * Serialize funding payload for API
   * Transforme les données du formulaire UI en payload API pour la queue
   */
  async serializeFunding(formData, userData = {}) {
    if (!formData) {
      throw new Error('Funding form data is required');
    }

    const formattedPhone = this._formatPhoneNumber(formData.phoneNumber);
    const userId = formData.userId || (userData.id ? userData.id : 'unknown');
    const amount = this._parseAmount(formData.amount);
    const currency = formData.currency || WALLET_DEFAULT_CURRENCY;
    const paymentMethod = formData.paymentMethod;

    const provider = await getProviderToUse(true);
    if (!provider) {
      throw new Error(
        "Impossible de retirer de l'argent. Veuillez réessayer plus tard."
      );
    }

    const payload = {
      id: `trx_wallet_credit_${Date.now()}`,
      requestId: `req_wallet_credit_${Date.now()}`,
      type: 'walletCredit',
      src: {
        service: 'rapidoo',
        currency: currency,
        amount: amount,
        processor: paymentMethod?.toUpperCase(),
        country: 'BJ',
        firstName: formData.customer?.firstname || '',
        lastName: formData.customer?.lastname || '',
        phoneNumber: formattedPhone
      },
      dst: {
        wallet: `wallet_${userId}`,
        service: 'rapidoo',
        processor: 'rapidoWallet',
        currency: currency,
        amount: amount,
        country: 'any'
      },
      metadata: {
        provider: provider,
        ownerId: userId,
        narration: 'funding Wallet'
      }
    };

    // Enrichir le payload avec les informations utilisateur si disponibles
    if (userData && userData.id) {
      if (userData.lastName) payload.src.lastName = userData.lastName;
    }
    return payload;
  }

  /**
   * Serialize withdrawal payload for API
   * Transforme les données du formulaire UI en payload API pour la queue
   */
  async serializeWithdrawal(formData, userData = {}) {
    console.log(
      '🚀 ~ WalletSerializer ~ serializeWithdrawal ~ formData:',
      formData
    );
    if (!formData) {
      throw new Error('Withdrawal form data is required');
    }

    const formattedPhone = this._formatPhoneNumber(formData.phoneNumber);
    const userId = formData.userId || (userData.id ? userData.id : undefined);
    const amount = this._parseAmount(formData.amount);
    const currency = formData.currency || WALLET_DEFAULT_CURRENCY;
    const paymentMethod = formData.paymentMethod;

    // Calcul des frais dynamiques
    const fees = await calculateFee(amount);

    if (!fees) {
      throw new Error(
        'Impossible de calculer les frais de retrait. Veuillez réessayer ultérieurement.'
      );
    }

    const provider = await getProviderToUse(false);
    if (!provider) {
      throw new Error(
        "Impossible de retirer de l'argent. Veuillez réessayer plus tard."
      );
    }

    const data = {
      id: `trx_wallet_debit_${Date.now()}`,
      requestId: `req_wallet_debit_${Date.now()}`,
      type: 'walletDebit',
      src: {
        wallet: `wallet_${userId}`,
        service: 'rapidoo',
        processor: 'rapidoWallet',
        currency: currency,
        amount: fees.srcAmount,
        country: 'any'
      },
      dst: {
        service: 'rapidoo',
        currency: currency,
        amount: fees.dstAmount,
        processor: paymentMethod?.toUpperCase(),
        country: 'BJ',
        firstName: formData.customer?.firstname || '',
        lastName: formData.customer?.lastname || '',
        phoneNumber: formattedPhone
      },
      metadata: {
        provider: provider,
        source: 'user',
        wallet: `wallet_${userId}`,
        ownerId: `${userId}`,
        narration: formData.reason || 'withdraw Wallet',
        fees: {
          srcAmount: fees.srcAmount,
          dstAmount: fees.dstAmount,
          totalFeeCalculated: fees.totalFeeCalculated,
          feePercent: fees.feePercent,
          feeFix: fees.feeFix,
          feePercentCalculated: fees.feePercentCalculated,
          applyFeeOnSrc: fees.applyFeeOnSrc
        }
      }
    };

    // Enrichir le payload avec les informations utilisateur si disponibles
    if (userData && userData.id) {
      data.metadata.ownerId = userData.id;
      data.src.wallet = `wallet_${userData.id}`;
      if (userData.firstName) data.dst.firstName = userData.firstName;
      if (userData.lastName) data.dst.lastName = userData.lastName;
    }
    console.log('🚀 ~ WalletSerializer ~ serializeWithdrawal ~ data:', data);
    return data;
  }

  /**
   * Serialize account payload for Accounts API
   */
  serializeAccount(formData, userData = {}) {
    if (!formData) {
      throw new Error('Account data is required');
    }

    const userId = formData.userId || userData?.id || 'unknown';
    const formattedPhone = this._formatPhoneNumber(
      formData.phoneNumber || formData.rawPhoneNumber || ''
    );
    const accountLabel =
      formData.accountLabel ||
      formData.owner ||
      formData.accountOwnerName ||
      `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() ||
      'Compte Mobile Money';

    return {
      accountLabel: accountLabel,
      accountIsPhone: true,
      accountNumber: formattedPhone,
      wallet: `wallet_${userId}`,
      userId: userId,
      operator: (formData.operator || 'MTN').toUpperCase(),
      country: formData.country || 'BJ'
    };
  }

  /**
   * Serialize transaction query params for API
   */
  serializeTransactionQuery(params) {
    const queryParams = {};

    if (params?.type) {
      queryParams.type = params.type;
    }

    if (params?.status) {
      queryParams.status = params.status;
    }

    if (params?.page) {
      queryParams.page = params.page;
    }

    if (params?.limit) {
      queryParams.limit = params.limit;
    }

    if (params?.startDate) {
      queryParams.startDate = params.startDate;
    }

    if (params?.endDate) {
      queryParams.endDate = params.endDate;
    }

    return queryParams;
  }

  // ============================================
  // DESERIALIZE: API -> UI
  // ============================================

  /**
   * Deserialize balance response from API
   * Transforme la réponse API en données UI pour le solde
   */
  deserializeBalance(apiResponse) {
    const data = apiResponse?.data || apiResponse;
    console.log('🚀 ~ WalletSerializer ~ deserializeBalance ~ data:', data);

    // Gestion du cas 404 (pas de wallet)
    if (apiResponse?.statusCode === 404) {
      return {
        balance: 0,
        currency: WALLET_DEFAULT_CURRENCY,
        walletId: null,
        lastUpdated: new Date(),
        exists: false
      };
    }

    // Cas normal avec données
    return {
      balance: this._parseAmount(data?.currentBalance || data?.balance || 0),
      currency: data?.currency || WALLET_DEFAULT_CURRENCY,
      walletId: data?.walletId || null,
      lastUpdated: data?.updatedAt ? new Date(data.updatedAt) : new Date(),
      exists: true
    };
  }

  /**
   * Deserialize broadcast response from API
   * Transforme la réponse de la queue en données UI
   */
  deserializeBroadcastResponse(apiResponse) {
    const data = apiResponse?.data || apiResponse;

    return {
      transactionId: data?.payload?.id || null,
      event: data?.event || null,
      message: apiResponse?.message || '',
      status: WALLET_STATUS.PENDING // Toujours en attente via queue
    };
  }

  /**
   * Deserialize transactions list from API
   * Transforme la liste de transactions API en données UI
   */
  deserializeTransactions(apiResponse) {
    const data = apiResponse?.data || [];
    const transactions = (
      Array.isArray(data) ? data : data?.transactions || []
    ).map((txn) => this.deserializeTransaction(txn));

    const paginationSource = apiResponse || data;

    return {
      transactions,
      pagination: {
        page: paginationSource?.page || 1,
        limit: paginationSource?.limit || 20,
        total: paginationSource?.total || 0,
        totalPages: paginationSource?.totalPages || 0
      }
    };
  }

  /**
   * Deserialize single transaction from API
   * Transforme une transaction API en données UI
   */
  deserializeTransaction(apiTransaction) {
    console.log(
      '🚀 ~ WalletSerializer ~ deserializeTransaction ~ apiTransaction:',
      apiTransaction
    );
    let type = apiTransaction?.type || WALLET_TRANSACTION_TYPES.CREDIT;
    let amount = apiTransaction?.amount || 0;
    let paymentMethod = apiTransaction?.paymentMethod;

    const src = apiTransaction?.src || {};
    const dst = apiTransaction?.dst || {};

    if (dst?.processor === 'rapidoWallet' || dst?.wallet) {
      type = WALLET_TRANSACTION_TYPES.CREDIT;
      amount = dst?.amount || amount;
      paymentMethod = src?.processor || src?.provider || paymentMethod;
    } else if (src?.processor === 'rapidoWallet' || src?.wallet) {
      type = WALLET_TRANSACTION_TYPES.DEBIT;
      amount = src?.amount || amount;
      paymentMethod = dst?.processor || dst?.provider || paymentMethod;
    }

    // Extrait fees depuis metadata.fees ou metadata.fee si présent
    const rawFees =
      apiTransaction?.metadata?.fees || apiTransaction?.metadata?.fee || null;
    const fees = rawFees
      ? {
          srcAmount: rawFees.srcAmount ?? 0,
          dstAmount: rawFees.dstAmount ?? 0,
          totalFeeCalculated: rawFees.totalFeeCalculated ?? 0,
          feePercent: rawFees.feePercent ?? 0,
          feeFix: rawFees.feeFix ?? 0,
          feePercentCalculated: rawFees.feePercentCalculated ?? 0,
          applyFeeOnSrc: rawFees.applyFeeOnSrc ?? false
        }
      : null;

    return {
      id: apiTransaction?.id || null,
      type: type,
      amount: this._parseAmount(amount),
      currency:
        apiTransaction?.currency ||
        dst?.currency ||
        src?.currency ||
        WALLET_DEFAULT_CURRENCY,
      balanceBefore: this._parseAmount(apiTransaction?.balanceBefore || 0),
      balanceAfter: this._parseAmount(apiTransaction?.balanceAfter || 0),
      description:
        apiTransaction?.metadata?.narration ||
        apiTransaction?.description ||
        apiTransaction?.reason ||
        '',
      status: this._mapStatus(
        apiTransaction?.status || apiTransaction?.dstStatus
      ),
      paymentMethod: '',
      paymentReference:
        apiTransaction?.paymentReference || apiTransaction?.reference || null,
      phoneNumber:
        apiTransaction?.phoneNumber ||
        src?.phoneNumber ||
        dst?.phoneNumber ||
        null,
      createdAt: apiTransaction?.createdAt
        ? new Date(apiTransaction.createdAt)
        : new Date(),
      completedAt: apiTransaction?.completedAt
        ? new Date(apiTransaction.completedAt)
        : null,
      fees: fees,
      metadata: apiTransaction?.metadata || {}
    };
  }

  /**
   * Deserialize polling status from API
   * Transforme le statut de polling API en données UI
   */
  deserializePollingStatus(apiResponse) {
    const data = apiResponse?.data || apiResponse;
    const fedaPayStatus = data?.fedaPayStatus || data?.status;
    const isCompleted = [
      FEDAPAY_TRANSACTION_STATUS.APPROVED,
      FEDAPAY_TRANSACTION_STATUS.TRANSFERRED
    ].includes(fedaPayStatus);
    const isFailed = [
      FEDAPAY_TRANSACTION_STATUS.DECLINED,
      FEDAPAY_TRANSACTION_STATUS.CANCELLED
    ].includes(fedaPayStatus);

    return {
      transactionId: data?.transactionId || null,
      fedaPayTransactionId: data?.fedaPayTransactionId || null,
      status: isCompleted
        ? WALLET_STATUS.COMPLETED
        : isFailed
          ? WALLET_STATUS.FAILED
          : WALLET_STATUS.PENDING,
      fedaPayStatus: fedaPayStatus,
      completed: isCompleted || isFailed,
      attempts: data?.attempts || 0,
      maxAttempts: data?.maxAttempts || 30,
      newBalance: isCompleted
        ? this._parseAmount(data?.newBalance || data?.currentBalance || 0)
        : null,
      message: data?.message || ''
    };
  }

  /**
   * Deserialize accounts list from Accounts API
   */
  deserializeAccounts(apiResponse) {
    const data = apiResponse?.data?.data || apiResponse || [];
    const accountsList = Array.isArray(data) ? data : [];
    const accounts = accountsList.map((acc) => this.deserializeAccount(acc));

    return {
      accounts
    };
  }

  /**
   * Deserialize single account object from Accounts API
   */
  deserializeAccount(apiAccount) {
    return {
      id: apiAccount?.id || null,
      accountLabel: apiAccount?.accountLabel || 'Compte Mobile Money',
      accountNumber: apiAccount?.accountNumber || '',
      operator: apiAccount?.operator || 'MTN',
      country: apiAccount?.country || 'BJ',
      isVerified: apiAccount?.isVerified ?? true,
      isSuspended: apiAccount?.isSuspended ?? false,
      payoutDetails: apiAccount?.payoutDetails || {},
      createdAt: apiAccount?.createdAt
        ? new Date(apiAccount.createdAt)
        : new Date(),
      updatedAt: apiAccount?.updatedAt
        ? new Date(apiAccount.updatedAt)
        : new Date()
    };
  }

  // ============================================
  // ERROR SERIALIZATION
  // ============================================

  /**
   * Deserialize API error to user-friendly format
   */
  deserializeError(apiError, requestType) {
    const errorData = apiError?.data || apiError;
    const statusCode = apiError?.statusCode || apiError?.status || 500;

    return {
      code: this._mapErrorCode(statusCode, errorData?.error, requestType),
      message: this._mapErrorMessage(statusCode, errorData, requestType),
      statusCode,
      details: errorData
    };
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  _cleanPhoneNumber(phoneNumber) {
    if (!phoneNumber) return '';
    return phoneNumber.replace(/[\s\-\(\)]/g, '');
  }

  _formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) return '';
    let cleaned = this._cleanPhoneNumber(phoneNumber);
    if (cleaned.startsWith('+')) {
      cleaned = cleaned.substring(1);
    }
    if (!cleaned.startsWith('229')) {
      cleaned = '229' + cleaned;
    }
    return cleaned;
  }

  _parseAmount(amount) {
    if (typeof amount === 'number') return Math.round(amount);
    if (typeof amount === 'string') {
      return parseInt(amount.replace(/[,\s]/g, ''), 10) || 0;
    }
    return 0;
  }

  _mapStatus(apiStatus) {
    const statusMap = {
      pending: WALLET_STATUS.PENDING,
      approved: WALLET_STATUS.COMPLETED,
      transferred: WALLET_STATUS.COMPLETED,
      declined: WALLET_STATUS.FAILED,
      cancelled: WALLET_STATUS.FAILED,
      failed: WALLET_STATUS.FAILED,
      timeout: WALLET_STATUS.FAILED,
      completed: WALLET_STATUS.COMPLETED,
      processing: WALLET_STATUS.PROCESSING,
      success: WALLET_STATUS.COMPLETED,
      error: WALLET_STATUS.FAILED
    };

    return statusMap[apiStatus?.toLowerCase()] || WALLET_STATUS.PENDING;
  }

  _mapErrorCode(statusCode, apiError, requestType) {
    const errorMap = {
      400: WALLET_ERROR_CODES.INVALID_AMOUNT,
      401: WALLET_ERROR_CODES.NETWORK_ERROR,
      403: WALLET_ERROR_CODES.NETWORK_ERROR,
      404: WALLET_ERROR_CODES.WALLET_NOT_FOUND,
      409: WALLET_ERROR_CODES.INSUFFICIENT_BALANCE,
      422: WALLET_ERROR_CODES.INVALID_PHONE_NUMBER,
      429: WALLET_ERROR_CODES.DAILY_LIMIT_EXCEEDED,
      500: WALLET_ERROR_CODES.NETWORK_ERROR,
      503: WALLET_ERROR_CODES.PAYMENT_FAILED
    };

    if (requestType === 'accountCreation') {
      if (apiError.statusCode === 409) {
        return WALLET_ERROR_CODES.ACCOUNT_ALREADY_EXISTS;
      }
      if (statusCode === 404) {
        return WALLET_ERROR_CODES.INVALID_PHONE_NUMBER;
      }
    }

    return errorMap[statusCode] || WALLET_ERROR_CODES.NETWORK_ERROR;
  }

  _mapErrorMessage(statusCode, errorData, requestType) {
    if (requestType === 'accountCreation') {
      if (statusCode === 409) {
        return 'Ce compte Mobile Money est déjà enregistré et validé.';
      }
      if (statusCode === 404) {
        return "Ce numéro ne peut pas être utilisé comme compte de retrait. Vérifiez le numéro et l'opérateur.";
      }
    }

    const messageMap = {
      400: 'Données invalides. Veuillez vérifier votre saisie.',
      401: 'Non autorisé. Veuillez vous reconnecter.',
      403: 'Accès refusé.',
      404: 'Wallet non trouvé. Veuillez contacter le support.',
      409: 'Solde insuffisant pour cette opération.',
      422: 'Numéro de téléphone invalide.',
      429: 'Limite quotidienne dépassée. Réessayez demain.',
      500: 'Erreur serveur. Veuillez réessayer.',
      503: 'Service de paiement indisponible.'
    };

    if (errorData?.message) {
      return errorData.message;
    }

    return (
      messageMap[statusCode] || 'Une erreur est survenue. Veuillez réessayer.'
    );
  }

  // ============================================
  // FORMATTING METHODS
  // ============================================

  formatAmount(amount, currency = WALLET_DEFAULT_CURRENCY) {
    const parsedAmount = this._parseAmount(amount);
    return `${parsedAmount.toLocaleString('fr-FR')} ${currency}`;
  }

  formatDate(date) {
    if (!date) return '-';
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) return '';
    const cleaned = this._cleanPhoneNumber(phoneNumber);

    if (cleaned.startsWith('229') && cleaned.length === 11) {
      return `+229 ${cleaned.slice(3, 5)} ${cleaned.slice(5, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9)}`;
    }

    return cleaned;
  }

  getTransactionTypeLabel(type) {
    const labels = {
      [WALLET_TRANSACTION_TYPES.CREDIT]: 'Crédit',
      [WALLET_TRANSACTION_TYPES.DEBIT]: 'Débit'
    };
    return labels[type] || type;
  }

  getStatusLabel(status) {
    const labels = {
      [WALLET_STATUS.PENDING]: 'En attente',
      [WALLET_STATUS.COMPLETED]: 'Terminé',
      [WALLET_STATUS.FAILED]: 'Échoué',
      [WALLET_STATUS.PROCESSING]: 'En cours'
    };
    return labels[status] || status;
  }

  getPaymentMethodLabel(method) {
    const labels = {
      [WALLET_PAYMENT_METHODS.MOOV]: 'Moov',
      [WALLET_PAYMENT_METHODS.MTN]: 'MTN',
      [WALLET_PAYMENT_METHODS.CARD]: 'Carte',
      [WALLET_PAYMENT_METHODS.WALLET]: 'Wallet'
    };
    return labels[method?.toLowerCase()] || method;
  }
}

export async function calculateFee(amount) {
  try {
    const feeUrl =
      'https://api.monrapidoo.com/settings/byKey?group=ultraxeno&key=fees&service=rapidoo';
    const res = await fetch(feeUrl);

    if (!res.ok) {
      return null;
    }

    const responseData = await res.json();

    if (responseData?.statusCode !== 200 || !responseData?.data?.payout) {
      return null;
    }

    const payout = responseData.data.payout;
    const feePercent = payout.percent ?? 0;
    const feeFix = payout.fixedFee ?? 0;
    const applyFeeOnSrc = payout.applyFeeOnSrc ?? false;

    const feePercentCalculated = (amount * feePercent) / 100;
    const totalFeeCalculated = feePercentCalculated + feeFix;

    const srcAmount = applyFeeOnSrc ? amount + totalFeeCalculated : amount;
    const dstAmount = !applyFeeOnSrc ? amount - totalFeeCalculated : amount;

    return {
      srcAmount,
      dstAmount,
      totalFeeCalculated,
      feePercent,
      feeFix,
      feePercentCalculated,
      applyFeeOnSrc
    };
  } catch (error) {
    console.error('Erreur lors du calcul des frais :', error);
    return null;
  }
}

export async function getProviderToUse(isCollect) {
  try {
    const feeUrl =
      'https://api.monrapidoo.com/settings/byKey?group=ultraxeno&key=fees&service=rapidoo';
    const res = await fetch(feeUrl);

    if (!res.ok) {
      return null;
    }

    const responseData = await res.json();

    // Vérification du status code API et de la présence des paramètres requis
    if (
      responseData?.statusCode !== 200 ||
      !responseData?.data?.paymentProviders
    ) {
      return null;
    }

    const collectionProviders =
      responseData.data.paymentProviders.collectionProviders;
    const withdrawalProviders =
      responseData.data.paymentProviders.withdrawalProviders;

    if (isCollect) {
      return collectionProviders;
    } else {
      return withdrawalProviders;
    }
  } catch (error) {
    console.error(
      'Erreur lors de la récupération du fournisseur de paiement :',
      error
    );
    return null;
  }
}

export async function checkFedapayAccount(phoneNumber, operator = 'mtn') {
  try {
    let cleanPhone = String(phoneNumber || '')
      .replace(/\+/g, '')
      .replace(/\s+/g, '')
      .trim();
    if (cleanPhone.startsWith('229') && cleanPhone.length > 8) {
      cleanPhone = cleanPhone.slice(3);
    }
    const cleanOp = (operator || 'mtn').toLowerCase();
    const url = `https://api.monrapidoo.com/fedapay-pilot/account/check?phoneNumber=${cleanPhone}&operator=${cleanOp}&group=ultraxeno`;
    const res = await fetch(url);
    if (!res.ok) {
      return { exists: false, isVerified: false, hasError: true };
    }
    const json = await res.json();
    const data = json?.data || {};
    return {
      exists: !!data?.exists,
      isVerified: !!data?.isVerified,
      status: data?.status || '',
      hasError: !!data?.hasError,
      data: data
    };
  } catch (error) {
    console.error('Erreur lors de la vérification du compte FedaPay :', error);
    return { exists: false, isVerified: false, hasError: true, error };
  }
}

export async function getPayoutLimit(platform = 'general') {
  try {
    const feeUrl =
      'https://api.monrapidoo.com/settings/byKey?group=ultraxeno&key=fees&service=rapidoo';
    const res = await fetch(feeUrl);

    if (!res.ok) {
      return { min: 1000, max: 10000 };
    }

    const responseData = await res.json();

    if (responseData?.statusCode !== 200 || !responseData?.data?.amountLimits) {
      return { min: 1000, max: 10000 };
    }

    const limits = responseData.data.amountLimits;
    const normalizedPlatform = (platform || '').toLowerCase();

    let payoutLimit = null;
    if (normalizedPlatform === 'rider' || normalizedPlatform === 'payoutrider') {
      payoutLimit = limits.payoutRider || limits.payout;
    } else if (
      normalizedPlatform === 'restaurant' ||
      normalizedPlatform === 'payoutrestaurant' ||
      normalizedPlatform === 'merchant'
    ) {
      payoutLimit = limits.payoutRestaurant || limits.payout;
    } else {
      payoutLimit = limits.payout;
    }

    return {
      min: payoutLimit?.min ?? 1000,
      max: payoutLimit?.max ?? 10000,
      rawLimits: limits
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des limites de retrait :', error);
    return { min: 1000, max: 10000 };
  }
}

const walletSerializer = new WalletSerializer();
walletSerializer.getProviderToUse = getProviderToUse;
walletSerializer.checkFedapayAccount = checkFedapayAccount;
walletSerializer.calculateFee = calculateFee;
walletSerializer.getPayoutLimit = getPayoutLimit;

export default walletSerializer;
